import React, { useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, UserRound, Mail, Phone, Pencil, Check, X } from "lucide-react";
import { L, Card, SectionTitle } from "./shared";

const EMPTY = { user_id: "", name: "", role: "", email: "", phone: "" };

export default function TeamTab({ pid, cfg, meta, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editF, setEditF] = useState(EMPTY);
  const employees = meta?.employees || [];

  async function refresh() { const { data } = await api.get(`/staff/client-config/${pid}`); onSaved(data); }

  function pickEmployee(id) {
    const e = employees.find(x => x.id === id);
    setF(e ? { user_id: id, name: e.name || "", role: e.job_title || "", email: e.email || "", phone: e.phone || "" } : { ...EMPTY });
  }
  async function add() {
    if (!f.name.trim()) { toast.error("Numele este obligatoriu"); return; }
    setBusy(true);
    try { await api.post(`/staff/client-config/${pid}/contacts`, { ...f, user_id: f.user_id || null }); setF(EMPTY); await refresh(); toast.success("Contact adăugat"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  }
  async function saveEdit(c) {
    try { await api.put(`/staff/client-config/${pid}/contacts/${c.id}`, { ...editF, user_id: editF.user_id || null }); setEditing(null); await refresh(); toast.success("Salvat"); }
    catch (e) { toast.error(apiErr(e)); }
  }
  async function del(c) {
    if (!window.confirm(`Elimini „${c.name}” din echipa afișată clientului?`)) return;
    try { await api.delete(`/staff/client-config/${pid}/contacts/${c.id}`); await refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  }

  const contacts = cfg.contacts || [];
  return (
    <div className="space-y-6" data-testid="cc-team">
      <Card>
        <SectionTitle sub="Persoanele afișate în tab-ul „Echipă” din portalul clientului. Project managerul principal se setează în Configurare portal.">Adaugă membru echipă</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <L label="Preia din angajați (opțional)">
              <select value={f.user_id} onChange={e => pickEmployee(e.target.value)} className="inp" data-testid="team-pick">
                <option value="">— completează manual —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}{e.job_title ? ` · ${e.job_title}` : ""}</option>)}
              </select>
            </L>
          </div>
          <L label="Nume"><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="inp" data-testid="team-name" /></L>
          <L label="Rol în proiect"><input value={f.role} onChange={e => setF({ ...f, role: e.target.value })} className="inp" placeholder="Ex: Project Manager" data-testid="team-role" /></L>
          <L label="Email"><input value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="inp" /></L>
          <L label="Telefon"><input value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="inp" /></L>
        </div>
        <button onClick={add} disabled={busy} data-testid="team-add" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-ink text-white text-sm rounded-lg hover:bg-brand disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Adaugă
        </button>
      </Card>

      <Card>
        <SectionTitle>Echipa afișată ({contacts.length})</SectionTitle>
        {cfg.pm && (
          <div className="mb-3 flex items-center gap-3 p-3 rounded-lg bg-brand/5 border border-brand/15 text-[13px]">
            <UserRound className="h-4 w-4 text-brand" /> <b>{cfg.pm.name}</b> <span className="text-ink-muted">· Project Manager principal · {cfg.pm.email}{cfg.pm.phone ? ` · ${cfg.pm.phone}` : ""}</span>
          </div>
        )}
        {contacts.length === 0 ? (
          <div className="py-8 text-center text-ink-muted text-sm border border-dashed border-neutral-200 rounded-lg">Niciun membru adăugat.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {contacts.map(c => (
              <div key={c.id} className="border border-neutral-200 rounded-xl p-4" data-testid={`team-row-${c.id}`}>
                {editing === c.id ? (
                  <div className="space-y-2">
                    <input value={editF.name} onChange={e => setEditF({ ...editF, name: e.target.value })} className="inp" />
                    <input value={editF.role} onChange={e => setEditF({ ...editF, role: e.target.value })} className="inp" placeholder="Rol" />
                    <input value={editF.email} onChange={e => setEditF({ ...editF, email: e.target.value })} className="inp" placeholder="Email" />
                    <input value={editF.phone} onChange={e => setEditF({ ...editF, phone: e.target.value })} className="inp" placeholder="Telefon" />
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setEditing(null)} className="p-1.5 text-ink-muted hover:text-ink"><X className="h-4 w-4" /></button>
                      <button onClick={() => saveEdit(c)} className="p-1.5 text-emerald-600"><Check className="h-4 w-4" /></button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0"><UserRound className="h-5 w-5" /></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-brand">{c.role || "—"}</div>
                      <div className="font-medium text-ink">{c.name}</div>
                      <div className="text-[12px] text-ink-muted flex flex-col mt-1">
                        {c.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {c.email}</span>}
                        {c.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</span>}
                      </div>
                    </div>
                    <button onClick={() => { setEditing(c.id); setEditF({ user_id: c.user_id || "", name: c.name, role: c.role || "", email: c.email || "", phone: c.phone || "" }); }} className="p-1.5 text-ink-muted hover:text-ink"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(c)} className="p-1.5 text-ink-muted hover:text-red-600" data-testid={`team-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
